import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import { setCredentials } from 'features/auth/authSlice';
import { markSoftHomeWelcomeSeen } from 'features/auth/api/authService';
import { ROUTES } from 'constants/routes';

/**
 * One-time congrats when an orphan learner receives platform soft-home affiliation.
 */
export function SoftHomeWelcomeModal() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const token = useAppSelector((s) => s.auth.token);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const open = Boolean(user?.softHomeWelcomePending && user?.softHomeInstitutionName);
  if (!open || !user) return null;

  const dismiss = async () => {
    setBusy(true);
    setError('');
    try {
      await markSoftHomeWelcomeSeen();
      const nextUser = { ...user, softHomeWelcomePending: false };
      if (token) {
        dispatch(setCredentials({ token, user: nextUser }));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not dismiss');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="soft-home-welcome-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-xl">
        <h2
          id="soft-home-welcome-title"
          className="font-display text-2xl font-semibold text-content"
        >
          Welcome aboard
        </h2>
        <p className="mt-3 text-sm text-content-secondary">
          Congratulations - you&apos;re successfully onboarded to{' '}
          <span className="font-semibold text-content">{user.softHomeInstitutionName}</span>.
        </p>
        <p className="mt-2 text-sm text-content-tertiary">
          Explore programs and request a seat when you&apos;re ready. Classroom access unlocks after
          enrollment.
        </p>
        {error ? (
          <p className="mt-2 text-sm text-danger" role="alert">
            {error}
          </p>
        ) : null}
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex min-h-11 items-center justify-center rounded-lg bg-accent px-4 py-2.5 text-sm font-medium text-accent-foreground hover:bg-accent-hover disabled:opacity-50"
            disabled={busy}
            onClick={() => void dismiss()}
          >
            {busy ? 'Please wait…' : 'Continue'}
          </button>
          <Link
            to={ROUTES.PROGRAMS}
            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-border bg-surface-secondary px-4 py-2.5 text-sm font-medium text-content hover:bg-surface-tertiary"
            onClick={() => void dismiss()}
          >
            Explore programs
          </Link>
        </div>
      </div>
    </div>
  );
}
